/**
 * Aanwezigheidskalender Page (Epic 4, Story 4.2, UX V6)
 *
 * Rijen (medewerkers) × dagen, met dag/week/maand-toggle (segmented
 * control, week = default) — herbruikt `ViewModeSelector`'s
 * interactiepatroon uit `features/werkrooster` (AD-9-conform: hergebruikt
 * de patroon-familie, geen incompatibele tweede navigatie-stijl). Rol-scope
 * en privacy-collapse komen kant-en-klaar terug van Story 4.1's
 * `calendarService` — deze pagina rendert simpelweg wat de API teruggeeft
 * (geen client-side privacy-logica).
 *
 * Responsief (AC 10): desktop-first; start op de 'dag'-view op smalle
 * viewports (mobiel-fallback, UX-spec §Responsief) — de gebruiker kan
 * daarna zelf naar week/maand schakelen.
 */

import { useEffect, useMemo, useState } from 'react';
import { Layout } from '../../components/Layout';
import { useAanwezigheidskalender } from './hooks/useAanwezigheidskalender';
import { AanwezigheidGrid } from './components/AanwezigheidGrid';
import { AanwezigheidTellingStrip } from './components/AanwezigheidTellingStrip';
import { AanwezigheidLegend } from './components/AanwezigheidLegend';
import { AanwezigheidFilters } from './components/AanwezigheidFilters';
import { afdelingKey, afdelingOpties } from '../../lib/afdeling';
import { matchesAanwezigheid, type AanwezigheidFilterMode } from './utils/telling';
import { ViewModeSelector, type PlanningViewMode } from '../werkrooster/components/ViewModeSelector';
import {
  getMondayOfWeek,
  getWorkweekDates,
  getWorkdaysOfMonth,
  addDays,
  addWeeks,
  addMonths,
  toDateString,
  formatDayLabel,
  formatDateRange,
  formatMonthLabel,
} from '../werkrooster/utils/dateUtils';

/** Label the range the grid actually shows (ma t/m vr), not the calendar week. */
function formatDateRangeLabel(dates: string[]): string {
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last) return '';
  return formatDateRange(new Date(`${first}T00:00:00`), new Date(`${last}T00:00:00`));
}

function useDatesForView(viewMode: PlanningViewMode, currentDate: Date): string[] {
  return useMemo(() => {
    if (viewMode === 'dag') return [toDateString(currentDate)];
    // Weekweergave toont alleen werkdagen (besluit Friso, 2026-07-29), net
    // als élk ander weekoverzicht in de portal — zie `getWorkweekDates`. De
    // dagtak filtert NIET, zodat vorige/volgende dag voorspelbaar één dag
    // opschuift in plaats van soms drie.
    if (viewMode === 'week') return getWorkweekDates(getMondayOfWeek(currentDate)).map(toDateString);
    return getWorkdaysOfMonth(currentDate.getFullYear(), currentDate.getMonth()).map(toDateString);
  }, [viewMode, currentDate]);
}

export function AanwezigheidPage() {
  const [viewMode, setViewMode] = useState<PlanningViewMode>('week');
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());

  // Mobiel-fallback (AC 10): start op dag-view op smalle viewports.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setViewMode('dag');
    }
  }, []);

  const dates = useDatesForView(viewMode, currentDate);
  const van = dates[0] ?? toDateString(currentDate);
  const tot = dates[dates.length - 1] ?? van;

  const { data, isLoading, isError } = useAanwezigheidskalender(van, tot);
  const rawResponse = data?.data;

  // ── Filters ──
  const [selectedDepartment, setSelectedDepartment] = useState<number | null>(null);
  const [filterMode, setFilterMode] = useState<AanwezigheidFilterMode>('alle');

  // Afdelingsopties uit de daadwerkelijke response (nooit hardcoden).
  // Medewerkers zonder afdeling krijgen een eigen bucket, zodat ze niet
  // stilletjes uit beeld vallen zodra er gefilterd wordt.
  const departments = useMemo<Array<[number, string]>>(
    () => (rawResponse ? afdelingOpties(rawResponse.employees.map((e) => e.employee)) : []),
    [rawResponse],
  );

  // Terugval TIJDENS het renderen, niet via een effect: een effect laat
  // eerst één frame met een lege grid zien voordat het corrigeert. De
  // keuze zelf blijft in state staan, zodat terugbladeren naar een periode
  // waar de afdeling wél voorkomt het filter gewoon herstelt.
  const effectiveDepartment =
    selectedDepartment !== null && departments.some(([id]) => id === selectedDepartment) ? selectedDepartment : null;

  // Eén keer opbouwen, niet per medewerker (zie `matchesAanwezigheid`).
  const dateSet = useMemo(() => new Set(dates), [dates]);

  const response = useMemo(() => {
    if (!rawResponse) return undefined;
    if (effectiveDepartment === null && filterMode === 'alle') return rawResponse;

    const employees = rawResponse.employees.filter((entry) => {
      if (effectiveDepartment !== null && afdelingKey(entry.employee) !== effectiveDepartment) {
        return false;
      }
      return matchesAanwezigheid(entry, dateSet, filterMode);
    });

    return { ...rawResponse, employees };
  }, [rawResponse, effectiveDepartment, filterMode, dateSet]);

  // Epic aanwezigheid-verlof-vrij, Story 1.1 (AC 2/AC 3, edge-case-review
  // finding): the aanwezigheidskalender now shows "Vrij" for EVERY
  // goedgekeurd-verlof-dagdeel, including the peer-path (`canSeeLeaveDetail`
  // false — the server strips `leaveType` there, calendarService.ts:253-255,
  // but `status` stays `verlof_goedgekeurd`). The legend must therefore show
  // its single "Vrij"-item whenever the view contains ANY approved leave,
  // not only when the viewer can see the concrete verloftype. Since
  // `AanwezigheidLegend` (and its `presentLeaveTypes: number[]` prop
  // signature, kept unchanged per tech-spec) only checks
  // `presentLeaveTypes.length > 0` after the Story 1.1 consolidation, a
  // peer-view with approved leave but no visible type numbers still needs a
  // non-empty array — `-1` is a presence-only placeholder (no real Nmbrs
  // verloftype is ever 0 or negative) for exactly that case.
  const presentLeaveTypes = useMemo(() => {
    if (!response) return [];
    const types = new Set<number>();
    let hasApprovedLeaveWithoutVisibleType = false;
    for (const emp of response.employees) {
      for (const day of emp.days) {
        for (const dagdeel of [day.morning, day.afternoon]) {
          if (dagdeel.status !== 'verlof_goedgekeurd') continue;
          if (dagdeel.leaveType !== null && dagdeel.leaveType !== undefined) {
            types.add(dagdeel.leaveType);
          } else {
            hasApprovedLeaveWithoutVisibleType = true;
          }
        }
      }
    }
    if (types.size > 0) return [...types].sort((a, b) => a - b);
    return hasApprovedLeaveWithoutVisibleType ? [-1] : [];
  }, [response]);

  const hasActiveFilters = effectiveDepartment !== null || filterMode !== 'alle';

  // De weekweergave toont sinds deze wijziging ma t/m vr, dus het bereik
  // moet op de laatst getoonde dag eindigen — `formatWeekRange` rekende
  // vanaf maandag +6 en zou "27 jul - 2 aug" boven een grid van 27 t/m 31
  // juli zetten. Label en inhoud spreken elkaar dan tegen.
  const rangeLabel =
    viewMode === 'dag'
      ? formatDayLabel(currentDate)
      : viewMode === 'week'
        ? formatDateRangeLabel(dates)
        : formatMonthLabel(currentDate);

  const goPrev = () => {
    if (viewMode === 'dag') setCurrentDate(addDays(currentDate, -1));
    else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, -1));
    else setCurrentDate(addMonths(currentDate, -1));
  };
  const goNext = () => {
    if (viewMode === 'dag') setCurrentDate(addDays(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addMonths(currentDate, 1));
  };
  const goToday = () => setCurrentDate(new Date());

  return (
    <Layout>
      <div data-testid="aanwezigheid-page" className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1E293B]">Aanwezigheidskalender</h1>
          <p className="mt-1 text-sm text-gray-500">
            {/* Epic aanwezigheid-verlof-vrij, Story 1.1 (adversarial-review
                finding): was "... wie werkt, is vrij of heeft verlof." —
                treated "vrij" en "verlof" als twee losse dingen, terwijl elk
                goedgekeurd-verlof-dagdeel in de kalender nu zélf al "Vrij"
                heet. Herformuleerd naar de exacte doelstelling uit de user
                story ("in één oogopslag zie wie er werkt en wie vrij is"),
                zodat de subtitel niet meer tegenspreekt wat de cellen tonen. */}
            {response?.scope === 'org' ? 'Organisatiebreed overzicht' : 'Teamoverzicht'} — wie werkt en wie vrij is.
          </p>
        </div>

        <div className="bg-white rounded-lg border border-[#E2E8F0]">
          <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between flex-wrap gap-3">
            <ViewModeSelector viewMode={viewMode} onViewModeChange={setViewMode} />
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button type="button" onClick={goPrev} aria-label="Vorige" className="px-2.5 py-1.5 text-gray-600 hover:bg-gray-100">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button type="button" onClick={goToday} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 border-x border-gray-200">
                  Vandaag
                </button>
                <button type="button" onClick={goNext} aria-label="Volgende" className="px-2.5 py-1.5 text-gray-600 hover:bg-gray-100">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <span className="text-sm font-medium text-gray-900">{rangeLabel}</span>
            </div>
          </div>

          {/* Ook zichtbaar houden als er wél gefilterd is maar geen data is
              (laadfout): anders kun je het filter dat alles verbergt niet
              meer wissen. */}
          {(response || hasActiveFilters) && (
            <div className="px-4 py-3 border-b border-[#E2E8F0]">
              <AanwezigheidFilters
                departments={departments}
                selectedDepartment={effectiveDepartment}
                onDepartmentChange={setSelectedDepartment}
                filterMode={filterMode}
                onFilterModeChange={setFilterMode}
              />
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5A7A]"></div>
              <span className="ml-3 text-sm text-gray-500">Kalender laden...</span>
            </div>
          ) : isError || !response ? (
            <div className="text-center py-8 text-sm text-gray-500">Kon de aanwezigheidskalender niet laden.</div>
          ) : hasActiveFilters && rawResponse!.employees.length > 0 && response.employees.length === 0 ? (
            // Alleen wanneer de filters het écht veroorzaken: was de response
            // zelf al leeg, dan blijft de grid's eigen "geen medewerkers"-
            // melding staan in plaats van de filters de schuld te geven.
            <div
              data-testid="aanwezigheid-geen-resultaten"
              className="text-center py-8 text-sm text-gray-500"
            >
              Geen medewerkers gevonden met de huidige filters.
            </div>
          ) : (
            <>
              <AanwezigheidGrid response={response} dates={dates} />
              <AanwezigheidTellingStrip response={response} dates={dates} />
              <AanwezigheidLegend presentLeaveTypes={presentLeaveTypes} />
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
